import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import styles from './LoginPage.module.css';
import Footer from '../components/Footer';
import { useSystemLogin } from '../contexts/SystemLoginContext';
import { createSystemLoginApiClient } from '../utils/systemLoginApi';
import { WECHAT_CONFIG } from '../config/wechat';

// Google API 类型声明
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: GoogleInitializeConfig) => void;
          renderButton: (element: HTMLElement | null, options: GoogleButtonOptions) => void;
          prompt: () => void;
        };
      };
    };
    WeixinJSBridge?: any;
    wx?: any;
    WxLogin?: any;
  }
}

interface GoogleInitializeConfig {
  client_id: string;
  callback: (response: GoogleResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleButtonOptions {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
  locale?: string;
}

interface GoogleResponse {
  credential: string;
  select_by: string;
}

interface WechatLoginResponse {
  code: string;
  state?: string;
}

interface LocationState {
  from?: string;
}

interface LoginErrors {
  phone?: string;
  password?: string;
  general?: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const systemLogin = useSystemLogin();
  const apiClient = createSystemLoginApiClient(systemLogin);
  
  const [errors, setErrors] = useState<LoginErrors>({});
  const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);
  const [isWechatLoading, setIsWechatLoading] = useState(false);
  const [showWechatQRCode, setShowWechatQRCode] = useState(false);
  const [isWechatScriptLoaded, setIsWechatScriptLoaded] = useState(false);
  


  // Google 登录回调
  const handleGoogleLogin = useCallback(async (response: GoogleResponse) => {
    try {
      console.log('Google 登录成功，获取到凭证:', response.credential);
      
      // 调用后端 API 验证 Google 凭证
      await apiClient.loginWithGoogle(response.credential);
      
      // 登录成功后跳转
      const from = (location.state as LocationState)?.from || '/';
      navigate(from, { replace: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '使用 Google 登录失败';
      console.error('Google 登录失败:', errorMessage);
      setErrors(prev => ({ ...prev, general: errorMessage }));
    }
  }, [navigate, location.state, apiClient]);

  // 微信登录处理
  const handleWechatLogin = useCallback(async () => {
    try {
      setIsWechatLoading(true);
      setErrors(prev => ({ ...prev, general: '' }));

      // 无论是否在微信内置浏览器，均使用二维码登录的统一逻辑
      setShowWechatQRCode(true);
      setIsWechatLoading(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '微信登录失败';
      console.error('微信登录失败:', errorMessage);
      setErrors(prev => ({ ...prev, general: errorMessage }));
      setIsWechatLoading(false);
    }
  }, [navigate, location.state, apiClient]);

  // 初始化 Google 登录按钮
  useEffect(() => {
    if (isGoogleScriptLoaded && window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: '782646984441-16jnhmrrb61fnemjacihckukp8u403v8.apps.googleusercontent.com',
        callback: handleGoogleLogin,
        auto_select: false,
        cancel_on_tap_outside: true
      });

      // 渲染按钮
      window.google.accounts.id.renderButton(
        document.getElementById('google-login-btn'),
        {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'center',
          width: 250,
          locale: 'zh_CN'
        }
      );
    }
  }, [isGoogleScriptLoaded, handleGoogleLogin]);

  // 动态加载 Google 身份服务脚本
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setIsGoogleScriptLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // 初始化微信二维码登录
  useEffect(() => {
    if (isWechatScriptLoaded && showWechatQRCode && window.WxLogin) {
      new window.WxLogin({
        self_redirect: false,
        id: 'wechat-qrcode-container',
        appid: WECHAT_CONFIG.APP_ID,
        scope: WECHAT_CONFIG.QRCODE_SCOPE,
        redirect_uri: WECHAT_CONFIG.REDIRECT_URI,
        state: WECHAT_CONFIG.STATE,
        style: 'black',
        href: '',
      });
      
      // 监听微信登录消息
      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== 'https://open.weixin.qq.com') return;
        
        const { data } = event;
        if (data && data.code) {
          console.log('微信扫码登录成功，获取到code:', data.code);
          
          // 调用登录API
          apiClient.loginWithWechat(data.code, WECHAT_CONFIG.APP_ID)
            .then(() => {
              const from = (location.state as LocationState)?.from || '/';
              navigate(from, { replace: true });
            })
            .catch((error) => {
              const errorMessage = error instanceof Error ? error.message : '微信登录失败';
              console.error('微信登录失败:', errorMessage);
              setErrors(prev => ({ ...prev, general: errorMessage }));
            });
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [isWechatScriptLoaded, showWechatQRCode, apiClient, navigate, location.state]);

  // 动态加载微信登录脚本
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
    script.async = true;
    script.onload = () => setIsWechatScriptLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  
  // 检查是否已经登录
  useEffect(() => {
    if (systemLogin.isLoggedIn) {
      const from = (location.state as LocationState)?.from || '/';
      navigate(from, { replace: true });
    }
  }, [systemLogin.isLoggedIn, navigate, location.state]);

  const handleBack = () => {
    navigate('/');
  };

  const handleCloseWechatQRCode = () => {
    setShowWechatQRCode(false);
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header className={styles.header}>
          <button onClick={handleBack} className={styles.backButton}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.backIcon}>
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            返回
          </button>
          <h1 className={styles.title}>登录</h1>
        </header>

        <main className={styles.main}>
          {showWechatQRCode ? (
            <div className={styles.loginCard}>
              <div className={styles.logoSection}>
                <h2 className={styles.welcomeTitle}>微信扫码登录</h2>
                <p className={styles.welcomeSubtitle}>请使用微信扫描下方二维码完成登录</p>
              </div>

              {errors.general && (
                <div className={styles.errorMessage}>
                  {errors.general}
                </div>
              )}

              <div style={{ 
                textAlign: 'center',
                padding: '20px 0'
              }}>
                <div id="wechat-qrcode-container" style={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  minHeight: '220px',
                  marginBottom: '20px'
                }}></div>
                
                <button
                  onClick={handleCloseWechatQRCode}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    color: '#666',
                    fontSize: '14px'
                  }}
                >
                  返回登录页面
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.loginCard}>
              <div className={styles.logoSection}>
                <h2 className={styles.welcomeTitle}>欢迎回来</h2>
                <p className={styles.welcomeSubtitle}>登录您的账户，开始设计玩具产品</p>
              </div>

              {errors.general && (
                <div className={styles.errorMessage}>
                  {errors.general}
                </div>
              )}

              <div className={styles.loginOptions}>
                <div 
                  id="google-login-btn" 
                  className={styles.googleLoginButton}
                  style={{ 
                    margin: '16px 0',
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: '40px'
                  }} 
                />
                
                <button
                  onClick={handleWechatLogin}
                  disabled={isWechatLoading}
                  className={styles.wechatLoginButton}
                >
                  {isWechatLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      登录中...
                    </div>
                  ) : (
                    <>
                      <Icon icon="tabler:brand-wechat" width="20" height="20" />
                      微信登录
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage; 